/**
 * Connectivity and sync state for the interface.
 *
 * One store, one derived status, one place the chip in the top bar reads from.
 * Everything the brief requires the interface to show visibly — Online, Offline,
 * Synchronising, Synchronisation failed, Last successful synchronisation time —
 * comes from here.
 */

import { create } from "zustand";

import { db, getSyncMeta } from "@/db/database";
import { deviceRepository } from "@/repositories/deviceRepository";
import { outboxRepository } from "@/repositories/outboxRepository";
import { ConnectivityMonitor, estimateClockSkewMs } from "@/sync/connectivity";
import { deriveStatus, type ConnectionStatus } from "@/sync/states";

interface SyncStoreState {
  /** null while the first probe is in flight — "Checking", not "Offline". */
  reachable: boolean | null;
  syncing: boolean;
  /** Operations waiting to reach the server. Shown as a count beside the chip. */
  pendingCount: number;
  /** Operations that exhausted their retries and need a human. Never discarded. */
  deadLetterCount: number;
  lastSyncedAt: string | null;
  clockSkewMs: number;

  status: ConnectionStatus;

  start: () => void;
  stop: () => void;
  refreshCounts: () => Promise<void>;
  /** Push whatever is queued. Safe to call at any time; re-entry is guarded. */
  drain: () => Promise<void>;
  /** Refresh this till's own copy of the catalogue. Safe to call at any time. */
  refreshCatalogue: () => Promise<void>;
  setSyncing: (syncing: boolean) => void;
}

const monitor = new ConnectivityMonitor();

/** Imported lazily: the auth store is the larger module, and only this path needs it. */
async function reconnectSession(): Promise<void> {
  try {
    const { useAuthStore } = await import("@/stores/authStore");
    await useAuthStore.getState().reconnect();
  } catch {
    // The next tick tries again.
  }
}

/**
 * How stale the till's catalogue copy may get while it has signal. A price
 * change in the back office reaches the offline grid within this long.
 */
const CATALOGUE_REFRESH_MS = 15 * 60_000;

export const useSyncStore = create<SyncStoreState>((set, get) => {
  let timer: number | null = null;
  let pruneTimer: number | null = null;
  /**
   * Recompute the single status the cashier sees.
   *
   * Derived rather than stored, so it cannot fall out of step with the inputs —
   * a chip reading "Online" while sales are stuck is worse than no chip at all.
   */
  const recompute = () => {
    const { reachable, syncing, deadLetterCount } = get();
    set({ status: deriveStatus({ reachable, syncing, deadLetterCount }) });
  };

  let unsubscribe: (() => void) | null = null;
  let pulling = false;
  let lastPullAt = 0;

  return {
    reachable: null,
    syncing: false,
    pendingCount: 0,
    deadLetterCount: 0,
    lastSyncedAt: null,
    clockSkewMs: 0,
    status: "CHECKING",

    start: () => {
      if (unsubscribe) return;

      unsubscribe = monitor.subscribe((reachable, result) => {
        const wasReachable = get().reachable;
        const skew = estimateClockSkewMs(result);
        set({ reachable, ...(skew !== null ? { clockSkewMs: skew } : {}) });
        recompute();

        // The connection just came back. Drain immediately rather than waiting
        // for the next tick: the moment a shopkeeper sees the chip turn green is
        // the moment they expect their morning's sales to be on their way.
        //
        // `!== true` rather than `=== false`: the first probe after start-up
        // goes from null to true, and that is just as much the moment to send
        // what was queued before the till was switched off — and to refresh the
        // catalogue copy the grid falls back to.
        if (reachable && wasReachable !== true) {
          // Reconnect first: a cashier who signed in offline has no token, and
          // the upload and the catalogue both need one.
          void reconnectSession().then(() => {
            void get().drain();
            void get().refreshCatalogue();
          });
        }
      });

      monitor.start();
      void get().refreshCounts();
      // Read the last successful sync from disk on start. Without this the
      // panel says "this till has not synced yet" after every reload, however
      // recently it synced — the store's copy is in memory and the page is not.
      void getSyncMeta()
        .then((meta) =>
          set({ lastSyncedAt: meta.lastSuccessfulSyncAt, clockSkewMs: meta.clockSkewMs }),
        )
        .catch(() => {
          // IndexedDB unavailable. The chip still reports connectivity, which is
          // the part a cashier acts on.
        });

      // A slow heartbeat behind the event. Reconnection events are not perfectly
      // reliable — a laptop waking from sleep, a captive portal that starts
      // answering — and a queue that only drains on an event can sit full for
      // hours with nothing visibly wrong.
      timer = window.setInterval(() => {
        if (get().reachable === false) return;
        void reconnectSession().then(() => get().drain());
        if (Date.now() - lastPullAt > CATALOGUE_REFRESH_MS) void get().refreshCatalogue();
        // The heartbeat rides the same tick. It is authenticated by the device
        // token rather than a user, so it keeps reporting after a shop has
        // closed with sales still queued — which is exactly the situation worth
        // knowing about, and exactly when nobody is signed in to notice.
        void deviceRepository.heartbeat();
      }, 60_000);

      // Trim acknowledged operations once an hour. Nothing was calling this —
      // the queue only ever grew, so a till that had been trading for a year
      // carried every operation it had ever sent in IndexedDB. Only SYNCED rows
      // past the retention window go; anything unacknowledged is untouched
      // however old, because "we could not upload this for a fortnight" is not a
      // reason to throw away a day's takings.
      pruneTimer = window.setInterval(() => {
        void outboxRepository.prune().then((removed) => {
          if (removed > 0) void get().refreshCounts();
        });
      }, 60 * 60_000);
    },

    stop: () => {
      monitor.stop();
      unsubscribe?.();
      unsubscribe = null;
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
      if (pruneTimer !== null) {
        window.clearInterval(pruneTimer);
        pruneTimer = null;
      }
    },

    /**
     * Push whatever is queued.
     *
     * Failures are deliberately swallowed. Every one is already recorded against
     * the operation itself — attempts, last error, next attempt — and the sync
     * centre is where somebody looks at them. An unhandled rejection here would
     * put a red banner over a till for something the system is already handling.
     */
    drain: async () => {
      if (get().syncing) return;

      set({ syncing: true });
      recompute();
      try {
        const { push } = await import("@/sync/engine");
        await push();
      } catch {
        // See above.
      } finally {
        set({ syncing: false });
        await get().refreshCounts();
        const meta = await getSyncMeta().catch(() => null);
        if (meta?.lastSuccessfulSyncAt) set({ lastSyncedAt: meta.lastSuccessfulSyncAt });
        recompute();
      }
    },

    /**
     * Pull the catalogue into IndexedDB.
     *
     * The Sales grid reads this copy whenever the server cannot be reached, so
     * it must not wait for somebody to press "Sync now" in the Sync Centre —
     * until this ran on its own, nothing else ever filled it. Failures are
     * swallowed for the same reason as `drain`: the next tick tries again.
     */
    refreshCatalogue: async () => {
      if (pulling) return;
      pulling = true;
      try {
        const { pull } = await import("@/sync/engine");
        await pull();
        lastPullAt = Date.now();
      } catch {
        // No signal, or signed out. The copy stays as it was.
      } finally {
        pulling = false;
      }
    },

    refreshCounts: async () => {
      try {
        const [pendingCount, deadLetterCount] = await Promise.all([
          db.pendingOperationCount(),
          db.deadLetterCount(),
        ]);
        set({ pendingCount, deadLetterCount });
        recompute();
      } catch {
        // IndexedDB can be unavailable — private browsing, a locked profile, a
        // quota failure. That must not take the interface down: the counts stay
        // at their last value and the chip keeps reporting connectivity.
      }
    },

    setSyncing: (syncing) => {
      set({ syncing });
      recompute();
    },
  };
});

/** Read-only selector for components that only need the chip's inputs. */
export const selectChipState = (state: SyncStoreState) => ({
  status: state.status,
  pendingCount: state.pendingCount,
  deadLetterCount: state.deadLetterCount,
  lastSyncedAt: state.lastSyncedAt,
});
